/*
THESIS: Property search should foreground useful evidence and clear actions, not an abstract browsing metaphor.
OWN-WORLD: Cool paper, midnight ink, signal blue, highlighter yellow, ruled facts, proof photography, and field-sheet geometry.
STORY: Set criteria → compare evidence-rich listings → inspect the details → save or inquire.
FIRST VIEWPORT: A compact search sheet sits beside three complete featured listings so the catalogue is immediately understandable.
FORM: “Open-house field guide,” assigned candidate 3, seed 93012a33; simplified into a split search/catalogue composition.
*/
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  ArrowRight,
  Building2,
  Check,
  ClipboardCheck,
  KeyRound,
  LandPlot,
  MapPinned,
} from "lucide-react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PropertyCard } from "@/components/property-display"
import { PropertySearchForm } from "@/components/search-form"
import { getHomeDataFn } from "@/lib/app.functions"
import type { PropertySearch, PropertyType } from "@/lib/types"

export const Route = createFileRoute("/")({
  loader: () => getHomeDataFn(),
  component: HomePage,
})

function HomePage() {
  const { viewer, featured, home, faq } = Route.useLoaderData()
  const navigate = useNavigate()
  const featuredProperties = featured.slice(0, 3)

  const handleSearch = (search: PropertySearch) =>
    navigate({ to: "/search", search })

  return (
    <>
      <section className="border-b-2 border-foreground">
        <div className="field-container grid min-h-[700px] lg:grid-cols-[minmax(340px,0.72fr)_minmax(0,1.28fr)]">
          <div className="border-border py-10 lg:border-r lg:pr-8">
            <p className="field-label">Property finder · live catalogue</p>
            <h1 className="field-heading mt-4 max-w-xl">
              Find the next place worth seeing.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-muted-foreground">
              Choose the essentials, compare complete listings, and keep saved
              properties and inquiries together in one demo workspace.
            </p>
            <div className="field-panel mt-8 p-5">
              <div className="mb-5 flex items-center justify-between border-b pb-3">
                <span className="field-label">01 · Search criteria</span>
                <Badge variant="outline" className="rounded-none">
                  Live catalogue
                </Badge>
              </div>
              <PropertySearchForm compact onSearch={handleSearch} />
            </div>
            <div className="mt-7 grid grid-cols-3 gap-px border bg-border">
              {[
                ["12", "demo listings"],
                ["5", "property types"],
                ["3", "account roles"],
              ].map(([value, label]) => (
                <div key={label} className="bg-background p-3">
                  <p className="font-heading text-2xl font-bold">{value}</p>
                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground uppercase">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="py-10 lg:pl-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="field-label">02 · Featured listings</p>
                <h2 className="mt-2 font-heading text-4xl leading-none font-semibold uppercase">
                  Properties ready to compare
                </h2>
              </div>
              <Link to="/search" className="field-link text-sm font-semibold">
                Browse all listings
              </Link>
            </div>
            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              {featuredProperties.map((property, index) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  signedIn={Boolean(viewer)}
                  className={
                    index === 0
                      ? "xl:col-span-2"
                      : "md:grid-cols-1 [&_img]:max-h-48"
                  }
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-2 border-foreground bg-accent p-4">
              <div>
                <p className="field-label text-foreground/65">
                  03 · Save and inquire
                </p>
                <p className="mt-1 font-heading text-xl font-bold uppercase">
                  Keep promising listings in one shortlist.
                </p>
              </div>
              <Button
                className="field-button bg-foreground text-white hover:bg-foreground/85"
                nativeButton={false}
                render={
                  <Link to={viewer ? "/favorites" : "/login"}>
                    {viewer ? "Open saved work" : "Use demo account"}
                  </Link>
                }
              >
                <ArrowRight />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="field-container py-20">
        <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]">
          <div>
            <p className="field-label">The method</p>
            <h2 className="field-heading mt-3">
              A clearer way through property search.
            </h2>
          </div>
          <div className="grid gap-px border bg-border sm:grid-cols-3">
            {[
              {
                icon: MapPinned,
                number: "01",
                title: "Search",
                copy: "Filter by use, region, price, size, and the details that matter.",
              },
              {
                icon: ClipboardCheck,
                number: "02",
                title: "Compare",
                copy: "Read photographs beside measured facts, amenities, and location context.",
              },
              {
                icon: KeyRound,
                number: "03",
                title: "Act",
                copy: "Save a shortlist, name the search, set alerts, or send one specific inquiry.",
              },
            ].map((step) => (
              <article
                key={step.title}
                className="field-rule min-h-72 bg-white p-6"
              >
                <div className="flex items-start justify-between">
                  <step.icon className="size-7 text-primary" />
                  <span className="font-heading text-4xl font-bold text-border">
                    {step.number}
                  </span>
                </div>
                <h3 className="mt-14 text-3xl uppercase">{step.title}</h3>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {step.copy}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y-2 border-foreground bg-foreground text-white">
        <div className="field-container grid lg:grid-cols-[1fr_2fr]">
          <div className="py-14 lg:border-r lg:border-white/20 lg:pr-10">
            <p className="field-label text-white/50">Ways into the catalogue</p>
            <h2 className="mt-3 font-heading text-5xl leading-none font-semibold uppercase">
              Search by the job the property must do.
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3">
            {[
              [Building2, "Residential", "residential"],
              [Building2, "Commercial", "commercial"],
              [LandPlot, "Land", "land"],
              [ClipboardCheck, "Investment", "investment"],
              [KeyRound, "Holiday rental", "holiday-rental"],
              [MapPinned, "Waterfront", "waterfront"],
            ].map(([Icon, label, type]) => (
              <Link
                key={String(label)}
                to="/search"
                search={
                  type === "waterfront"
                    ? { waterfront: true }
                    : { type: String(type) as PropertyType }
                }
                className="group min-h-40 border-r border-b border-white/20 p-5 hover:bg-primary"
              >
                <Icon className="size-6 text-accent" />
                <p className="mt-10 font-heading text-xl font-semibold uppercase">
                  {String(label)}
                </p>
                <ArrowRight className="mt-2 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="field-container grid gap-12 py-20 lg:grid-cols-2">
        <div>
          <p className="field-label">About this demo</p>
          <h2 className="mt-3 font-heading text-5xl leading-none font-semibold uppercase">
            {home[0]?.title ?? "One catalogue, all the working context."}
          </h2>
          <p className="mt-5 text-base leading-7 text-muted-foreground">
            {home[0]?.body}
          </p>
          <ul className="mt-7 space-y-3 text-sm font-semibold">
            {[
              "Synthetic listings and account activity",
              "Public member and administrator credentials",
              "Search, owner, editorial, and operations workflows",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="grid size-6 place-items-center bg-accent">
                  <Check className="size-4" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="field-label">Questions before getting in touch</p>
          <Accordion className="mt-3 border-t-2 border-foreground">
            {faq.map((item) => (
              <AccordionItem key={item.id} value={String(item.id)}>
                <AccordionTrigger className="rounded-none text-left font-heading text-xl font-semibold uppercase">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="max-w-xl text-sm leading-6 text-muted-foreground">
                  {item.body}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  )
}
